// El popup interrumpe el checkout una sola vez por sesión: queda marcado apenas
// se abre, sin importar si el cliente lo acepta, lo cierra o se va a la colección.
// El valor guardado es la oferta vigente (link o variante), así que si el cliente
// cambia la promo en los settings el popup se rearma solo.
const SEEN_KEY = 'upsellPopupSeen';

class UpsellPopup extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('.upsell-popup__dialog');
    this.primaryButton = this.querySelector('[data-upsell-popup-primary]');
    this.secondaryButton = this.querySelector('[data-upsell-popup-secondary]');
    this.errorEl = this.querySelector('[data-upsell-popup-error]');
    this.lastTrigger = null;
    this.checkoutUrl = this.dataset.checkoutUrl || '/checkout';
    this.mode = this.dataset.mode === 'link' ? 'link' : 'product';
    this.linkUrl = this.dataset.linkUrl || '';

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.onPrimary = this.onPrimary.bind(this);
    this.onSecondary = this.onSecondary.bind(this);

    document.addEventListener('click', this.handleDocumentClick, true);
    document.addEventListener('keydown', this.handleKeydown);

    this.querySelectorAll('[data-upsell-popup-close]').forEach((el) =>
      el.addEventListener('click', this.handleCloseClick)
    );
    // En modo link el CTA principal es un ancla común: navega solo, sin handler.
    if (this.mode !== 'link') {
      this.primaryButton.addEventListener('click', this.onPrimary);
    }
    this.secondaryButton.addEventListener('click', this.onSecondary);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick, true);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  async handleDocumentClick(event) {
    const trigger = event.target.closest(
      '#CartDrawer-Checkout, #checkout, .anchor--checkout, a[href$="/checkout"]'
    );
    if (!trigger) return;
    if (trigger.closest('[data-upsell-popup-skip]')) return;
    if (this.mode === 'link' ? !this.linkUrl : !this.dataset.variantId) return;
    if (trigger.disabled) return;
    // Allow modifier-clicks (cmd/ctrl/shift/middle-click) to behave normally
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.wasSeen()) {
      this.proceedToCheckout(trigger);
      return;
    }

    if (this.mode === 'link') {
      this.open(trigger);
      return;
    }

    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' }
      });
      const cart = await response.json();
      const variantId = String(this.dataset.variantId);

      if (cart.items.some((item) => String(item.variant_id) === variantId)) {
        this.proceedToCheckout(trigger);
        return;
      }
    } catch (e) {
      // If the cart check fails, fall back to opening the popup so the
      // merchant's upsell intent isn't silently dropped.
    }

    this.open(trigger);
  }

  get offerId() {
    return this.mode === 'link' ? this.linkUrl : String(this.dataset.variantId || '');
  }

  wasSeen() {
    try {
      return window.sessionStorage.getItem(SEEN_KEY) === this.offerId;
    } catch (e) {
      return false;
    }
  }

  rememberSeen() {
    try {
      window.sessionStorage.setItem(SEEN_KEY, this.offerId);
    } catch (e) {
      // Storage bloqueado (navegación privada): el popup se vuelve a mostrar.
    }
  }

  handleKeydown(event) {
    if (event.code === 'Escape' && this.hasAttribute('open')) {
      this.close();
    }
  }

  handleCloseClick(event) {
    event.preventDefault();
    this.close();
  }

  open(trigger) {
    this.rememberSeen();
    this.lastTrigger = trigger;
    this.removeAttribute('hidden');
    // force reflow so the [hidden] -> [open] transition runs
    void this.offsetWidth;
    this.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
    if (typeof trapFocus === 'function') {
      trapFocus(this.dialog, this.dialog);
    } else {
      this.dialog.focus();
    }
  }

  close() {
    this.removeAttribute('open');
    this.setAttribute('hidden', '');
    document.body.classList.remove('overflow-hidden');
    this.clearError();
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.lastTrigger);
    } else if (this.lastTrigger) {
      this.lastTrigger.focus();
    }
    this.lastTrigger = null;
  }

  async onPrimary() {
    this.clearError();
    this.setBusy(true);

    try {
      const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
      const item = {
        id: Number(this.dataset.variantId),
        quantity: Number(this.dataset.quantity) || 1
      };
      if (this.dataset.byPack) {
        item.properties = { _byPack: this.dataset.byPack };
      }
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: [item] })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      this.proceedToCheckout(this.lastTrigger);
    } catch (error) {
      this.setBusy(false);
      this.showError(error.message || 'No pudimos agregar el producto. Probá de nuevo.');
    }
  }

  onSecondary() {
    this.proceedToCheckout(this.lastTrigger);
  }

  proceedToCheckout(trigger) {
    // Anchor-based triggers (Vue cart): navigate to the anchor's href
    if (trigger && trigger.tagName === 'A' && trigger.href) {
      window.location.href = trigger.href;
      return;
    }
    // Button inside an anchor (Vue cart wraps button in anchor)
    const anchor = trigger && trigger.closest && trigger.closest('a[href]');
    if (anchor) {
      window.location.href = anchor.href;
      return;
    }
    // Native Shopify form-based checkout buttons
    const form = trigger && trigger.form;
    if (form) {
      const submitInput = document.createElement('input');
      submitInput.type = 'hidden';
      submitInput.name = 'checkout';
      submitInput.value = '';
      form.appendChild(submitInput);
      form.submit();
      return;
    }
    window.location.href = this.checkoutUrl;
  }

  setBusy(busy) {
    if (busy) {
      this.primaryButton.setAttribute('aria-busy', 'true');
      this.primaryButton.disabled = true;
      this.secondaryButton.disabled = true;
      this.querySelector('.upsell-popup__spinner').removeAttribute('hidden');
    } else {
      this.primaryButton.removeAttribute('aria-busy');
      this.primaryButton.disabled = false;
      this.secondaryButton.disabled = false;
      this.querySelector('.upsell-popup__spinner').setAttribute('hidden', '');
    }
  }

  showError(message) {
    this.errorEl.textContent = message;
    this.errorEl.removeAttribute('hidden');
  }

  clearError() {
    this.errorEl.textContent = '';
    this.errorEl.setAttribute('hidden', '');
  }
}

customElements.define('upsell-popup', UpsellPopup);
